/**
 * The transport of the playback mode: a playhead over a recorded round.
 *
 * Deliberately dumb — it moves one number. Everything the audience sees is
 * derived from that number by `stateAt()`, which means seeking backwards is
 * as exact as playing forwards, and the stage cannot drift out of step with
 * the clocks the way a step-by-step animation would.
 *
 * The tick is 40 ms rather than a requestAnimationFrame: the live show
 * repaints on the hub's broadcasts (a couple of dozen a second at most), so
 * matching that rate replays the same rhythm and keeps the annotated story's
 * margin measurement off the critical path.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PlaybackSpeed } from '../../shared/playback';

const TICK_MS = 40;

/**
 * What the transport needs to know about what it is playing: how long it is,
 * and where the stretches are that „Leerlauf überspringen“ may jump. The
 * projection replay passes the recording's idle gaps; a clip passes none,
 * because its plan has already compressed them.
 */
export interface PlayerSource {
  totalMs: number;
  gaps: { from: number; to: number }[];
}

export interface Player {
  playheadMs: number;
  playing: boolean;
  speed: PlaybackSpeed;
  /** Jump over stretches where nothing happened at all. */
  skipIdle: boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seek: (ms: number) => void;
  nudge: (deltaMs: number) => void;
  setSpeed: (s: PlaybackSpeed) => void;
  setSkipIdle: (v: boolean) => void;
  /** True once the playhead has run to the end of the recording. */
  finished: boolean;
}

export function usePlayer(source: PlayerSource | null): Player {
  const [playheadMs, setPlayhead] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<PlaybackSpeed>(1);
  const [skipIdle, setSkipIdle] = useState(false);

  const headRef = useRef(0);
  headRef.current = playheadMs;
  const gapsRef = useRef<{ from: number; to: number }[]>([]);

  useEffect(() => {
    gapsRef.current = source?.gaps ?? [];
    setPlayhead(0);
    setPlaying(false);
  }, [source]);

  const clamp = useCallback((ms: number) => Math.min(Math.max(0, ms), source?.totalMs ?? 0), [source]);

  useEffect(() => {
    if (!playing || !source) return;
    let last = performance.now();
    const id = setInterval(() => {
      const now = performance.now();
      const dt = (now - last) * speed;
      last = now;
      let next = headRef.current + dt;
      if (skipIdle) {
        const gap = gapsRef.current.find((g) => next > g.from && next < g.to);
        if (gap) next = gap.to;
      }
      if (next >= source.totalMs) {
        headRef.current = source.totalMs;
        setPlayhead(source.totalMs);
        setPlaying(false);
        return;
      }
      headRef.current = next;
      setPlayhead(next);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [playing, speed, skipIdle, source]);

  const seek = useCallback(
    (ms: number) => {
      const v = clamp(ms);
      headRef.current = v;
      setPlayhead(v);
    },
    [clamp],
  );

  const play = useCallback(() => {
    if (!source) return;
    // Pressing play on a finished recording starts it over — the usual
    // expectation, and the only sensible reading of "noch einmal".
    if (headRef.current >= source.totalMs) seek(0);
    setPlaying(true);
  }, [source, seek]);

  return {
    playheadMs,
    playing,
    speed,
    skipIdle,
    play,
    pause: () => setPlaying(false),
    toggle: () => (playing ? setPlaying(false) : play()),
    seek,
    nudge: (deltaMs) => seek(headRef.current + deltaMs),
    setSpeed,
    setSkipIdle,
    finished: source !== null && playheadMs >= source.totalMs,
  };
}
